import { useInfiniteQuery } from '@tanstack/react-query';
import { getVariationEventsTimeline } from '../api/sitesApi';
import { useAuthStore } from '../../auth/stores/authStore';
import type { VariationEventsTimelineResponse } from '../types/variationTimeline';

export const variationTimelineKeys = {
  all: ['variationTimeline'] as const,
  lists: () => [...variationTimelineKeys.all, 'list'] as const,
  list: (
    siteId: number,
    plotId: number,
    variationId: string,
    isAuthenticated: boolean
  ) =>
    [
      ...variationTimelineKeys.lists(),
      { siteId, plotId, variationId, isAuthenticated },
    ] as const,
};

const ITEMS_PER_PAGE = 20;

export interface UseVariationEventsTimelineReturn {
  events: VariationEventsTimelineResponse['items'];
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  loadMore: () => void;
}

/**
 * Hook to fetch variation events timeline with infinite scroll pagination
 */
export function useVariationEventsTimeline(
  siteId: number | null,
  plotId: number | null,
  variationId: string | null
): UseVariationEventsTimelineReturn {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: variationTimelineKeys.list(
      siteId ?? 0,
      plotId ?? 0,
      variationId ?? '',
      isAuthenticated
    ),
    queryFn: async ({ pageParam = 0 }) => {
      if (!siteId || !plotId || !variationId) {
        throw new Error('siteId, plotId, and variationId are required');
      }

      return await getVariationEventsTimeline(siteId, plotId, variationId, {
        skip: pageParam,
        take: ITEMS_PER_PAGE,
      });
    },
    getNextPageParam: lastPage => {
      return lastPage?.hasMore ? lastPage.skip + lastPage.take : undefined;
    },
    initialPageParam: 0,
    enabled:
      isAuthenticated &&
      siteId !== null &&
      siteId > 0 &&
      plotId !== null &&
      plotId > 0 &&
      variationId !== null &&
      variationId.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  const events = data?.pages.flatMap(page => page?.items ?? []) ?? [];
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  return {
    events,
    totalCount,
    hasMore: hasNextPage ?? false,
    isLoading,
    isLoadingMore: isFetchingNextPage,
    error: error as Error | null,
    loadMore: fetchNextPage,
  };
}
